import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Eye, 
  ChevronLeft, 
  ChevronRight,
  X,
  MapPin,
  Phone,
  User,
  FileText,
  Calendar,
  DollarSign,
  Loader2,
  AlertCircle,
  Download
} from 'lucide-react';
import axios from 'axios';

function Phase4Dashboard({ userId, period, customStartDate, customEndDate }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchDeliveries();
  }, [userId, period, customStartDate, customEndDate]);

  async function fetchDeliveries() {
    try {
      setLoading(true);
      setError(null);

      const params = {
        user_id: userId,
        period: period
      };

      if (period === 'custom' && customStartDate && customEndDate) {
        params.custom_start = customStartDate;
        params.custom_end = customEndDate;
      }

      const response = await axios.get(`${API_URL}/api/admin/province-deliveries`, {
        params
      });

      if (response.data.success) {
        setDeliveries(response.data.deliveries);
      }
    } catch (err) {
      console.error('Error fetching province deliveries:', err);
      setError('Error al cargar las entregas de provincia');
    } finally {
      setLoading(false);
    }
  }

  async function handleExportExcel() {
    try {
      setExporting(true);

      const params = {
        user_id: userId,
        period: period
      };

      if (period === 'custom' && customStartDate && customEndDate) {
        params.custom_start = customStartDate;
        params.custom_end = customEndDate;
      }

      const response = await axios.get(`${API_URL}/api/admin/province-deliveries/export`, {
        params,
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
      link.setAttribute('download', `entregas_provincia_${dateStr}.xlsx`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Error al exportar a Excel. Por favor, intenta nuevamente.');
    } finally {
      setExporting(false);
    }
  }

  async function handleViewImages(delivery) {
    try {
      setImageLoading(true);
      setSelectedDelivery(delivery);
      setCurrentImageIndex(0);

      const response = await axios.get(
        `${API_URL}/api/admin/province-delivery-images/${delivery.id}`
      );

      if (response.data.success) {
        setImages(response.data.images);
      }
    } catch (err) {
      console.error('Error fetching images:', err);
      setImages([]);
    } finally {
      setImageLoading(false);
    }
  }

  function closeModal() {
    setSelectedDelivery(null);
    setImages([]);
    setCurrentImageIndex(0);
  }

  function nextImage() {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }

  function prevImage() {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }

  function getStatusBadge(status) {
    const statusConfig = {
      pending: { label: 'Pendiente', class: 'bg-yellow-100 text-yellow-800' },
      in_transit: { label: 'En Tránsito', class: 'bg-blue-100 text-blue-800' },
      delivered: { label: 'Entregado', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelado', class: 'bg-red-100 text-red-800' },
      returned: { label: 'Devuelto', class: 'bg-gray-100 text-gray-800' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold font-montserrat ${config.class}`}>
        {config.label}
      </span>
    );
  }

  function truncateText(text, maxLength = 15) {
    if (!text) return '-';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  }

  function formatCurrency(amount) {
    return `S/ ${parseFloat(amount || 0).toFixed(2)}`;
  }

  function formatDate(dateString) {
    if (!dateString) return '-';
    
    // Parse the date string manually to avoid timezone conversion
    const [year, month, day] = dateString.split('-');
    
    // Create date object in local timezone
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString('es-PE', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
    });
    }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#B1336E] animate-spin" />
        <span className="ml-3 text-slate-600 font-montserrat">Cargando entregas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-red-800 font-montserrat">Error</h3>
          <p className="text-sm text-red-700 font-montserrat">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-1 w-full">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#B1336E]/10 rounded-lg">
                <Package className="w-6 h-6 text-[#B1336E]" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-montserrat">Total Entregas</p>
                <p className="text-2xl font-extrabold text-[#B1336E] font-montserrat">
                  {deliveries.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#67A43C]/10 rounded-lg">
                <DollarSign className="w-6 h-6 text-[#67A43C]" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-montserrat">Total Cargo</p>
                <p className="text-2xl font-extrabold text-[#67A43C] font-montserrat">
                  {formatCurrency(deliveries.reduce((sum, d) => sum + parseFloat(d.charge || 0), 0))}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#E6B43C]/10 rounded-lg">
                <DollarSign className="w-6 h-6 text-[#E6B43C]" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-montserrat">Total Adelanto</p>
                <p className="text-2xl font-extrabold text-[#E6B43C] font-montserrat">
                  {formatCurrency(deliveries.reduce((sum, d) => sum + parseFloat(d.deposit_advance || 0), 0))}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-montserrat">Total Cobrado</p>
                <p className="text-2xl font-extrabold text-blue-600 font-montserrat">
                  {formatCurrency(deliveries.reduce((sum, d) => sum + parseFloat(d.charged || 0), 0))}
                </p>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={handleExportExcel}
          disabled={exporting || deliveries.length === 0}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#67A43C] to-[#67A43C]/80 text-white font-semibold font-montserrat rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {exporting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Exportando...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Exportar a Excel</span>
            </>
          )}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Tienda</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Destinatario</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Teléfono</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Producto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Notas</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Provincia</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Distrito</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Dirección Agencia</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Código Recojo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Cargo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Adelanto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Cobrado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Fecha Entrega</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold font-montserrat uppercase tracking-wider">Imágenes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {deliveries.length === 0 ? (
                <tr>
                  <td colSpan="16" className="px-4 py-8 text-center text-slate-500 font-montserrat">
                    No hay entregas de provincia en el período seleccionado
                  </td>
                </tr>
              ) : (
                deliveries.map((delivery) => (
                  <tr key={delivery.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 font-montserrat">
                      #{delivery.id}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      {delivery.username || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      {delivery.recipient_name || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      {delivery.recipient_phone || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      <span title={delivery.product}>
                        {truncateText(delivery.product, 20)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      <span title={delivery.notes}>
                        {truncateText(delivery.notes, 15)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      {delivery.province || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      {delivery.district || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      <span title={delivery.agency_address}>
                        {truncateText(delivery.agency_address, 20)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      {delivery.agency_pickup_code || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 font-montserrat">
                      {formatCurrency(delivery.charge)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 font-montserrat">
                      {formatCurrency(delivery.deposit_advance)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900 font-montserrat">
                      {formatCurrency(delivery.charged)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 font-montserrat">
                      {formatDate(delivery.delivery_date)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {getStatusBadge(delivery.status)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => handleViewImages(delivery)}
                        className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors"
                        title="Ver imágenes"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Image Modal */}
      {selectedDelivery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-[#B1336E] to-[#E6B43C]">
              <h2 className="text-xl font-extrabold text-white font-montserrat flex items-center gap-2">
                <Package className="w-6 h-6" />
                Entrega #{selectedDelivery.id} - {selectedDelivery.recipient_name}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Delivery Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <User className="w-5 h-5 text-[#B1336E] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-600 font-montserrat">Tienda</p>
                    <p className="text-sm font-semibold text-slate-900 font-montserrat">
                      {selectedDelivery.username}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="w-5 h-5 text-[#B1336E] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-600 font-montserrat">Teléfono</p>
                    <p className="text-sm font-semibold text-slate-900 font-montserrat">
                      {selectedDelivery.recipient_phone}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-[#B1336E] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-600 font-montserrat">Ubicación</p>
                    <p className="text-sm font-semibold text-slate-900 font-montserrat">
                      {selectedDelivery.province}, {selectedDelivery.district}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-[#B1336E] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-600 font-montserrat">Fecha Entrega</p>
                    <p className="text-sm font-semibold text-slate-900 font-montserrat">
                      {formatDate(selectedDelivery.delivery_date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg md:col-span-2">
                  <FileText className="w-5 h-5 text-[#B1336E] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-600 font-montserrat">Producto</p>
                    <p className="text-sm font-semibold text-slate-900 font-montserrat">
                      {selectedDelivery.product}
                    </p>
                  </div>
                </div>

                {selectedDelivery.notes && (
                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg md:col-span-2">
                    <FileText className="w-5 h-5 text-[#B1336E] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-slate-600 font-montserrat">Notas</p>
                      <p className="text-sm text-slate-900 font-montserrat">
                        {selectedDelivery.notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Images Carousel */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-extrabold text-[#B1336E] font-montserrat mb-4">
                  Imágenes ({images.length})
                </h3>

                {imageLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-[#B1336E] animate-spin" />
                  </div>
                ) : images.length === 0 ? (
                  <div className="text-center py-12 text-slate-500 font-montserrat">
                    No hay imágenes disponibles
                  </div>
                ) : (
                  <div className="relative">
                    {/* Main Image */}
                    <div className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden">
                      <img
                        src={images[currentImageIndex].image_url}
                        alt={`Imagen ${currentImageIndex + 1}`}
                        className="w-full h-full object-contain"
                      />
                      
                      {/* Navigation Buttons */}
                      {images.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                          >
                            <ChevronLeft className="w-6 h-6" />
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                          >
                            <ChevronRight className="w-6 h-6" />
                          </button>
                        </>
                      )}

                      {/* Image Counter */}
                      <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/70 text-white rounded-full text-sm font-semibold font-montserrat">
                        {currentImageIndex + 1} / {images.length}
                      </div>
                    </div>

                    {/* Thumbnails */}
                    {images.length > 1 && (
                      <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                        {images.map((image, index) => (
                          <button
                            key={image.id}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                              index === currentImageIndex
                                ? 'border-[#B1336E] scale-105'
                                : 'border-slate-300 hover:border-[#E6B43C]'
                            }`}
                          >
                            <img
                              src={image.image_url}
                              alt={`Miniatura ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Phase4Dashboard;